export default {
  title: {
    id: 'rm_mine-my-phone-story12_name',
    defaultMessage:
      'How one Zimbabwe mine sparked a responsible mining movement',
  },
  excerpt: {
    id: 'rm_mine-my-phone-story12_excerpt',
    defaultMessage:
      'With demand for ethically sourced minerals growing, the Initiative for Responsible Mining (IRMA) and the Zimbabwe Environmental Law Association (ZELO) have partnered up to protect the social and environmental rights of local communities. Here, we uncover the story of the Unki Mine and how it’s trying to improve conditions for the workers, neighbours, and the land itself.',
  },
  s1P1: {
    id: 'rm_mine-my-phone-story12_s1_p1',
    defaultMessage:
      'In Zimbabwe, mining means big business. Home to over 60 minerals and raw materials, including sought-after Fast Tech resources like gold, lithium, and copper, it is [one of the most resource-rich](https://miningzimbabwe.com/minerals-and-locations-found-in-zimbabwe/) nations on Earth.',
  },
  s1P2: {
    id: 'rm_mine-my-phone-story12_s1_p2',
    defaultMessage:
      'This mineral wealth is critical for the country. In fact, according to the country’s [Deputy Minister of Industry and Commerce](https://www.heraldonline.co.zw/mining-remains-key-pillar-of-zimbabwes-economy-deputy-minister-modi/), Raj Modi, “Mining remains one of the key pillars of Zimbabwe’s economy.” Beyond providing jobs and industrial growth, it supplies critical raw materials that are essential for manufacturing and infrastructure globally.',
  },
  s2Title: {
    id: 'rm_mine-my-phone-story12_s2_title',
    defaultMessage: 'How mining caused tension with the community',
  },
  s2P1: {
    id: 'rm_mine-my-phone-story12_s2_p1',
    defaultMessage:
      'In the small town of Shurugwi, in southern Zimbabwe, platinum is one of the most valuable resources. And as the energy transition accelerates away from fossil fuels and toward renewable options, there is [now an unprecedented](https://matthey.com/documents/161599/3147297/7635_JMP_PT_Whitepaper_v9.pdf/801ba76f-73db-b3af-817c-a06b1915c199?t=1688633964960) demand for this raw material, with platinum catalysts being [used to power electric vehicles](https://www.angloamerican.com/our-portfolio/origins/platinum) without noise or harmful CO2 emissions.',
  },
  s2P2: {
    id: 'rm_mine-my-phone-story12_s2_p2',
    defaultMessage:
      'Extreme [weather conditions like droughts and floods](https://climateprojectexplorer.org/documents/ay4sf-adolescents-and-youth-for-a-sustainable-future-strengthening-climate-change-adaptation-and-community-resilience-through-green-jobs-in-zimbabwe_d14b?cfn=extreme+weather) – fueled by climate change – have impacted the local ecosystem, leading to agriculture becoming a less reliable source of work and income. Mining jobs, as a result, have become even more important. So, when the Unki Mine began its full operations in 2011, the local community was excited by a new source of opportunity.',
  },
  s2P3: {
    id: 'rm_mine-my-phone-story12_s2_p3',
    defaultMessage:
      'Owned then by [Anglo American](https://www.angloamerican.com/) (now [Valterra Platinum](https://www.valterraplatinum.com/)), Unki sits on Zimbabwe’s Great Dyke, a stretch of land that’s rich in mining history. Over the last decade – according to the Shurugwi communities ESG Report – this company has employed [over 2,000](https://responsiblemining.net/wp-content/uploads/2025/09/Unki_Case-Study_August2025.pdf) people; closed pits that were dangerous for communities and their livestock and offered vocational training and employment opportunities for women and youths, all in a bid to leave a positive legacy and set a precedent for responsible mining in Zimbabwe.',
  },
  s3P1: {
    id: 'rm_mine-my-phone-story12_s3_p1',
    defaultMessage:
      'This has provided the community a lifeline, but a [“source of deep tension”](https://responsiblemining.net/wp-content/uploads/2025/09/Unki_Case-Study_August2025.pdf) remains. “It’s money, everyone knows that,” said Fadzai Midzi, who is a project officer at [ZELO](https://zela.org/), Zimbabwe’s Environmental Law Organization.',
  },
  s3P2: {
    id: 'rm_mine-my-phone-story12_s3_p2',
    defaultMessage:
      '“Unki Mine is the only large-scale mining company in the area, so there’s a huge amount of expectation placed on it. People expect it will bring jobs and development. When they see heavy equipment moving in and out, they assume that will translate into better roads, running water and other improvements in their communities. When those expectations aren’t met, that’s what creates a lot of tension.”',
  },
  s4Title: {
    id: 'rm_mine-my-phone-story12_s4_title',
    defaultMessage: 'The environmental impact of platinum mining',
  },
  s4P1: {
    id: 'rm_mine-my-phone-story12_s4_p1',
    defaultMessage:
      'The environmental impact of platinum mining is huge. Its ore holds so little metal that [vast amounts of rock must be mined](https://www.bgr.bund.de/SharedDocs/Produkte_en/Downloads/Informationen_Nachhaltigkeit/pgm_en.pdf?__blob=publicationFile&v=1) and processed to keep up with demand, and this process uses extensive amounts of electricity. According to Midzi, dust pollution fills the air due to heavy machinery carrying the metals to local processing plants or markets.',
  },
  s4P2: {
    id: 'rm_mine-my-phone-story12_s4_p2',
    defaultMessage:
      'The Shurugwi communities ESG Report also states that poor regulation of artisanal miners has caused pollution of the river. “A seepage from Unki’s tailing dam into a water source”, Midzi added, “but there were no reports of anyone falling sick.” And with locals growing crops or rearing animals on contaminated land, locals were at risk of serious [health issues from exposure to](https://pmc.ncbi.nlm.nih.gov/articles/PMC11316267/) toxic pollutants.',
  },
  s4P3: {
    id: 'rm_mine-my-phone-story12_s4_p3',
    defaultMessage:
      'This is where a nonprofit like ZELO jumps into action. Since December 2019 they have worked alongside The Initiative for Responsible Mining Assurance (IRMA), who are setting a global standard for more responsible mining. As [IRMA self-proclaims](https://responsiblemining.net/wp-content/uploads/2025/09/Unki_Case-Study_August2025.pdf): “this is not just about emissions or tick-box safety, but the full picture: labor, land, community, biodiversity, water, human rights. It brings companies, workers, communities, and civil society to the same table, and it doesn’t just ask for compliance — it pushes for better.”',
  },
  s5Title: {
    id: 'rm_mine-my-phone-story12_s5_title',
    defaultMessage:
      'How IRMA bridged the gap between the mine and the community',
  },
  s5P1: {
    id: 'rm_mine-my-phone-story12_s5_p1',
    defaultMessage:
      'It was IRMA who helped rebuild trust between the Unki Mine and the community. Locals were trepidatious because of the mining industry’s reputation. “People assumed corruption... they weren’t aware of what the mining companies were doing,” said Midzi.',
  },
  s5P2: {
    id: 'rm_mine-my-phone-story12_s5_p2',
    defaultMessage:
      'Unki was one of the first large mining groups to submit a site for IRMA’s full audit off their own back. This was a way for the company to improve conditions for the workers, neighbors and the land itself. “We were quite keen to take part,” said Antony Chadhliwa, IRMA Lead at Unki.',
  },
  s5P3: {
    id: 'rm_mine-my-phone-story12_s5_p3',
    defaultMessage:
      '“We wanted to strengthen our systems, our relationships with communities, and improve how we manage the environment. We saw it as a way to improve how we do things across the board.” ZELO then conducted awareness sessions with locals, explaining what an IRMA audit meant. They created a safe space where people could speak freely, feedback was recorded, and local concerns were heard and directed to the correct authorities.',
  },
  s5P4: {
    id: 'rm_mine-my-phone-story12_s5_p4',
    defaultMessage:
      '“It’s no longer just about giving pressure, but helping companies become compliant... so they have that social license to operate,” added Midzi.',
  },
  s6Title: {
    id: 'rm_mine-my-phone-story12_s6_title',
    defaultMessage:
      'What opportunities has the Unki Mine provided for the community?',
  },
  s6P1: {
    id: 'rm_mine-my-phone-story12_s6_p1',
    defaultMessage:
      'During the audit, community members came with [longstanding concerns](https://responsiblemining.net/wp-content/uploads/2025/09/Unki_Case-Study_August2025.pdf) around legacy resettlement (it’s common for [people in mining towns to be rehomed](https://miningzimbabwe.com/building-a-just-framework-key-lessons-for-mining-displacement-and-resettlement/) without consultation), access to better roads, access to water, and the continued fight for better employment practices and benefits.',
  },
  s6P2: {
    id: 'rm_mine-my-phone-story12_s6_p2',
    defaultMessage:
      '[According to IRMA](https://responsiblemining.net/wp-content/uploads/2025/09/Unki_Case-Study_August2025.pdf), Unki listened. Boreholes were drilled, school blocks built and local employment opportunities expanded. “Unki Mine has delivered significant community development through livelihood initiatives, skills training, support for schools, and infrastructure partnerships, although concerns remain about how the benefits are distributed,” says Midzi. “I can’t really say we’ve got there, we’re still trying to work on it.”',
  },
  s6P3: {
    id: 'rm_mine-my-phone-story12_s6_p3',
    defaultMessage:
      'As the IRMA case study published in 2025 concurs, “grievances didn’t vanish,” but people stopped sending off formal written complaints because they acknowledged ZELO’s awareness sessions were a more direct route of communication.',
  },
  s6P4: {
    id: 'rm_mine-my-phone-story12_s6_p4',
    defaultMessage:
      '“The community now wants to protect Unki,” one local said in the report. “Because if we protect the mine, we’re protecting ourselves.”',
  },
  s7Title: {
    id: 'rm_mine-my-phone-story12_s7_title',
    defaultMessage: 'Marking a change for the industry',
  },
  s7P1: {
    id: 'rm_mine-my-phone-story12_s7_p1',
    defaultMessage:
      'Anglo American putting themselves forward to be audited by IRMA signaled a shift in the industry. There is now [growing awareness from consumers](https://www.hrw.org/news/2023/06/14/eus-new-supply-chain-laws-over-reliant-self-certification) and manufacturers about how raw materials are extracted and the human, geopolitical and environmental impacts they leave behind.',
  },
  s7P2: {
    id: 'rm_mine-my-phone-story12_s7_p2',
    defaultMessage:
      '“That pressure alone is what is causing some mining companies to really demonstrate that their value chains are clean, they are mining responsibly, and also those who are sourcing also want to source responsibly,” said Midzi.',
  },
  s7P3: {
    id: 'rm_mine-my-phone-story12_s7_p3',
    defaultMessage:
      'The impact of the Unki Mine audit has caused a ripple effect within the industry. Now, other mining companies in Zimbabwe, including Chinese-owned lithium producer Kamativi Mining Company and the [Zimbabwe Consolidated Diamond Company](https://www.zcdco.com/), are seeking IRMA certification, with continued partnership and support from ZELO. Though challenges remain to make places like the Unki Mine more commonplace across Africa’s extractivism movement, this particular example could mark the beginning of a more responsible future for the region.',
  },
  f1P1: {
    id: 'rm_mine-my-phone-story12_f1_p1',
    defaultMessage:
      'Matte casting at the new smelter at Anglo American Platinum’s Unki Mine in Zimbabwe. Credit: Creamer Media',
  },
  f2P1: {
    id: 'rm_mine-my-phone-story12_f2_p1',
    defaultMessage:
      'Anglo American Platinum mining site. Shurugwi, Zimbabwe. Credit: DRA Global',
  },
  f3P1: {
    id: 'rm_mine-my-phone-story12_f3_p1',
    defaultMessage: 'Unki Mine, Zimbabwe. Credit: Creamer Media',
  },
  f4P1: {
    id: 'rm_mine-my-phone-story12_f4_p1',
    defaultMessage:
      'Anglo American Platinum mining site. Shurugwi, Zimbabwe. January 2007 - December 2013. Credit: DRA Global',
  },
  q1: {
    id: 'rm_mine-my-phone-story12_q1',
    defaultMessage:
      '“Unki Mine is the only large-scale mining company in the area, so…people expect it will bring jobs and development. When those expectations aren’t met, that’s what creates a lot of tension.”',
  },
  q1Attr: {
    id: 'rm_mine-my-phone-story12_q1_attr',
    defaultMessage:
      '**Fadzai Midzi**, project officer at [ZELO](https://zela.org/), Zimbabwe’s Environmental Law Organization.',
  },
  q2: {
    id: 'rm_mine-my-phone-story12_q2',
    defaultMessage:
      '“The community now wants to protect Unki, because if we protect the mine, we’re protecting ourselves.”',
  },
  q2Attr: {
    id: 'rm_mine-my-phone-story12_q2_attr',
    defaultMessage: 'Shurugwi local resident',
  },
} as const
